import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules } from '@utils';
import { useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { globalActions, userActions, useStore } from '@modules';
import { PhoneFormatsType } from '@modules/global/types';
import { ProtectNextEnum, VerifyNextEnum } from '@modules/types';

import { Button } from '@components/Button';
import Captcha from '@components/Captcha';
import Phone from '@components/Phone';
import MainLayout from '@layouts/MainLayout';

import style from './ChangePhone.scss';

const cn = bemCssModules(style, 'ChangePhone');

const { verifyEmail, verifyPhone } = VerifyNextEnum;
const { verifyOTP } = ProtectNextEnum;

const ChangePhone: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState<PhoneFormatsType>('ru');
  const location = useLocation<LocationStateType['verification']>();
  const { state } = location;

  const [{ user, global }, dispatch] = useStore();
  const { errors, data: userData } = user;
  const { isShowCaptcha, captcha, countries } = global;

  const defaultCountry = userData?.country_code || global.dataByIp?.code;
  const currentCountry = countries.find(country => country.code === countryCode);
  const phoneNumber = `+${currentCountry?.phone_prefix}${phone.replace(/ /g, '')}`;

  useEffect(() => {
    dispatch(globalActions.getCountries());
  }, []);

  useEffect(() => {
    if (defaultCountry) setCountryCode(defaultCountry);
  }, [defaultCountry]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isShowCaptcha) dispatch(globalActions.setShowCaptcha(false));

    dispatch(userActions.changePhone({ phone: phoneNumber, captcha }))
      .unwrap()
      .then(({ data }) => {
        if (data.next && [verifyEmail, verifyPhone, verifyOTP].includes(data.next)) {
          const strongPassword = !!state?.strongPassword;
          const nextPage = CONST.nav.profileEdit;
          const flow =
            data.next === verifyOTP
              ? 'ChangePhoneVerifyOTP'
              : data.next === verifyEmail
              ? 'changePhoneVerifyEmail'
              : 'changePhoneVerifyPhone';

          navigate<'verifications'>(CONST.nav.verification, {
            state: {
              strongPassword,
              flow,
              token: data.token,
              nextPage,
              value: phoneNumber,
            },
          });
        }
      });
  };

  const handleChangePhone = (value: string) => {
    if (errors?.data?.phone?.length) dispatch(userActions.clearErrors());
    setPhone(value);
  };

  const isLoading = !!user.loaders.changePhone;

  const error = errors?.data?.phone?.join('\n');

  const onBack = () => navigate(CONST.nav.profileEdit);

  return (
    <MainLayout className={cn(null, [className])} backButton={onBack}>
      <div className={cn('content')}>
        <h1 className={cn('title')}>{t('account.change_phone')}</h1>
        <form className={cn('form')} onSubmit={handleSubmit}>
          <Phone
            className={cn('form')}
            phone={phone}
            isLoading={isLoading}
            countryCode={countryCode}
            setPhone={handleChangePhone}
            setCountryCode={setCountryCode}
            error={error}
            countries={countries}
          />
          {isShowCaptcha && <Captcha className={cn('captcha')} />}

          <Button className={cn('button')} type="submit" isLoading={isLoading} disabled={!phone}>
            {t('public.get_the_sms_code')}
          </Button>
        </form>
      </div>
    </MainLayout>
  );
};

export default ChangePhone;
